import { useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, Circle, CircleMarker, Tooltip, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import "leaflet.heat";
import { toast } from "sonner";
import client from "../lib/api";
import { Warning, MapPin, Calendar, Users, UserPlus, PaperPlaneTilt, Check, NavigationArrow } from "@phosphor-icons/react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

// Old-money gradient: cream → gold → oxblood (cool coverage → hot gap risk)
const HEAT_GRADIENT = { 0.0: "rgba(233,199,126,0)", 0.25: "#E9C77E", 0.5: "#C9A24A", 0.75: "#B0603A", 1.0: "#7E1119" };

function HeatLayer({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    const layer = L.heatLayer(points, {
      radius: 58, blur: 42, max: 1.0, minOpacity: 0.28, maxZoom: 12, gradient: HEAT_GRADIENT,
    }).addTo(map);
    return () => { map.removeLayer(layer); };
  }, [points, map]);
  return null;
}

const STATUS = {
  gap: { bg: "bg-[#7E1119]", soft: "bg-[#7E1119]/10 text-[#7E1119] border-[#7E1119]/30", label: "Gap" },
  tight: { bg: "bg-[#B08D57]", soft: "bg-[#B08D57]/15 text-[#8a6a3a] border-[#B08D57]/40", label: "Tight" },
  ok: { bg: "bg-emerald-600", soft: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "OK" },
  holiday: { bg: "bg-[#1A1715]/30", soft: "bg-[#1A1715]/8 text-[#1A1715]/60 border-[#1A1715]/15", label: "Holiday" },
};

function FitBounds({ points }) {
  const map = useMap();
  const sig = points.map((p) => `${p.lat},${p.lon}`).join("|");
  const lastSig = useRef(null);
  useEffect(() => {
    if (lastSig.current === sig) return; // coords unchanged: don't fight the user's zoom/pan
    lastSig.current = sig;
    if (points.length === 1) map.setView([points[0].lat, points[0].lon], 10);
    else if (points.length > 1) map.fitBounds(points.map((p) => [p.lat, p.lon]), { padding: [40, 40], maxZoom: 11 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig, map]);
  return null;
}

function CoverageMap({ photographers, regions, openShoots }) {
  const phPts = (photographers || []).filter((p) => p.lat != null && p.lon != null);
  const rgPts = (regions || []).filter((r) => r.lat != null && r.lon != null);
  const osPts = (openShoots || []).filter((s) => s.lat != null && s.lon != null);
  const all = [...phPts, ...rgPts, ...osPts];
  if (!all.length) return <p className="text-sm text-[#1A1715]/50" data-testid="coverage-map-empty">Set a base ZIP on your photographers to see the live coverage map.</p>;
  // Gap-risk heat: hotter where demand is high AND coverage is thin. Demand ramps the
  // blob; status weights it so gaps glow oxblood and covered regions stay cool gold.
  const heatPts = rgPts.map((r) => {
    const sev = r.status === "gap" ? 1.0 : r.status === "tight" ? 0.6 : 0.28;
    const demandRamp = 0.55 + 0.45 * Math.min(1, (r.demand || 1) / 4);
    return [r.lat, r.lon, Math.max(0.15, Math.min(1, sev * demandRamp))];
  });
  const osColor = { gap: "#7E1119", tight: "#B08D57", ok: "#2E7D5B" };
  return (
    <div className="rounded-xl overflow-hidden border border-[#1A1715]/10" style={{ height: 400 }} data-testid="coverage-map">
      <MapContainer center={[all[0].lat, all[0].lon]} zoom={9} className="coverage-leaflet" style={{ height: "100%", width: "100%" }} scrollWheelZoom={false}>
        <TileLayer attribution="&copy; OpenStreetMap &copy; CARTO" url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png" />
        {phPts.map((p, i) => (
          <Circle key={`ph${i}`} center={[p.lat, p.lon]} radius={(p.radius || 50) * 1609.34}
            pathOptions={{ color: "#2E4F3B", weight: 1, dashArray: "4 5", fillColor: "#2E4F3B", fillOpacity: 0.05 + 0.12 * (p.avail_ratio ?? 0.5) }}>
            <Tooltip>{p.name} · {p.radius} mi · {Math.round((p.avail_ratio ?? 0) * 100)}% available</Tooltip>
          </Circle>
        ))}
        {/* Gap-coverage heat projection (replaces singular region dots) */}
        <HeatLayer points={heatPts} />
        {/* Invisible hover targets keep the per-region tooltips without visible dots */}
        {rgPts.map((r, i) => (
          <CircleMarker key={`rg${i}`} center={[r.lat, r.lon]} radius={14}
            pathOptions={{ color: "transparent", weight: 0, fillColor: "#000", fillOpacity: 0 }}>
            <Tooltip>{r.city || r.zip} · {r.demand} shoot(s) · {r.status}</Tooltip>
          </CircleMarker>
        ))}
        {/* Open, unstaffed shoots — pinned & colored by staffing severity */}
        {osPts.map((s) => (
          <CircleMarker key={`os${s.id}`} center={[s.lat, s.lon]} radius={s.severity === "gap" ? 9 : 7}
            pathOptions={{ color: "#fff", weight: 1.5, fillColor: osColor[s.severity], fillOpacity: 0.95 }}>
            <Tooltip>
              <b>{s.title}</b><br />
              {s.needs_photographer && `Photog in range: ${s.photographers_in_range}`}
              {s.needs_photographer && s.needs_pilot && " · "}
              {s.needs_pilot && `Pilot in range: ${s.pilots_in_range}`}
            </Tooltip>
          </CircleMarker>
        ))}
        <FitBounds points={all} />
      </MapContainer>
    </div>
  );
}

export default function CoverageDashboard({ onOpen }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(14);
  const [refreshKey, setRefreshKey] = useState(0);
  const [assignFor, setAssignFor] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setLoading(true);
    client.get("/booking/coverage", { params: { days } })
      .then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [days, refreshKey]);

  const reload = () => setRefreshKey((k) => k + 1);

  const doAssign = async (slot, uid) => {
    if (!assignFor) return;
    setBusy(true);
    try {
      const field = slot === "photographer" ? "photographer_id" : "pilot_id";
      await client.patch(`/properties/${assignFor.id}`, { [field]: uid });
      toast.success(`Assigned ${slot}`);
      setAssignFor(null);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't assign crew");
    } finally { setBusy(false); }
  };

  const doInvite = async (slot, uid) => {
    if (!assignFor) return;
    setBusy(true);
    try {
      const r = await client.post(`/properties/${assignFor.id}/invite-crew`, { user_id: uid, slot });
      toast.success(`Invited ${r.data.invited}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send invite");
    } finally { setBusy(false); }
  };

  if (loading) return <p className="text-sm text-[#1A1715]/50" data-testid="coverage-loading">Analyzing coverage&hellip;</p>;
  if (!data) return <p className="text-sm text-[#7E1119]">Couldn't load coverage.</p>;

  return (
    <div className="space-y-8 max-w-4xl" data-testid="coverage-panel">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#B08D57] font-semibold mb-1">Operations</p>
          <h2 className="text-2xl font-bold text-[#1A1715]">Coverage &amp; Gaps</h2>
          <p className="text-sm text-[#1A1715]/60 mt-1">Where and when photographer availability is thin, by region &amp; date. {data.with_base}/{data.total_active} photographers have a base ZIP set.</p>
        </div>
        <select data-testid="coverage-window" value={days} onChange={(e) => setDays(Number(e.target.value))}
          className="border border-[#1A1715]/15 rounded-md px-3 py-2 text-sm bg-white">
          <option value={7}>Next 7 days</option><option value={14}>Next 14 days</option><option value={30}>Next 30 days</option>
        </select>
      </div>

      {/* Missing-data nudges (base ZIP + shoots without a ZIP) */}
      {(data.total_active - data.with_base > 0 || data.no_zip_shoots > 0) && (
        <div className="space-y-2" data-testid="coverage-warnings">
          {data.total_active - data.with_base > 0 && (
            <div data-testid="warn-missing-base" className="flex items-start gap-2 border border-[#B08D57]/40 bg-[#B08D57]/10 rounded-lg p-3 text-sm text-[#8a6a3a]">
              <Warning size={16} weight="fill" className="text-[#B08D57] mt-0.5 shrink-0" />
              <span><b>{data.total_active - data.with_base} photographer(s) have no base ZIP.</b> They won&rsquo;t appear on the map or get matched to nearby shoots{data.no_base_photographers?.length ? `: ${data.no_base_photographers.join(", ")}` : ""}. Ask them to set it in Profile.</span>
            </div>
          )}
          {data.no_zip_shoots > 0 && (
            <div data-testid="warn-missing-zip-shoots" className="flex items-start gap-2 border border-[#7E1119]/30 bg-[#7E1119]/8 rounded-lg p-3 text-sm text-[#7E1119]">
              <Warning size={16} weight="fill" className="mt-0.5 shrink-0" />
              <span><b>{data.no_zip_shoots} upcoming shoot(s) have no ZIP</b> and can&rsquo;t be mapped or coverage-checked. Add a ZIP to those shoots.</span>
            </div>
          )}
        </div>
      )}

      {/* Coverage map — photographer radius + availability (prioritized view) */}
      <div>
        <h3 className="text-sm font-semibold text-[#1A1715] mb-3 flex items-center gap-2"><MapPin size={16} weight="fill" className="text-[#B08D57]" /> Coverage map</h3>
        <CoverageMap photographers={data.photographers} regions={data.regions} openShoots={data.open_shoots} />
        <div className="flex flex-wrap items-center gap-4 text-xs text-[#1A1715]/60 mt-2">
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-20 h-3 rounded-full border border-[#1A1715]/10" style={{ background: "linear-gradient(90deg,#E9C77E,#C9A24A,#B0603A,#7E1119)" }} />
            Gap-risk projection (gold = covered · oxblood = thin/gap)
          </span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full border border-dashed border-[#2E4F3B] bg-[#2E4F3B]/10" /> Photographer coverage radius</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-[#7E1119] border border-white" /> Open shoot · no crew in range</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-[#B08D57] border border-white" /> Open shoot · thin coverage</span>
        </div>
      </div>

      {/* Uncovered open shoots — staffing holes in the open pool */}
      <div data-testid="uncovered-open-shoots">
        <h3 className="text-sm font-semibold text-[#1A1715] mb-3 flex items-center gap-2">
          <Warning size={16} weight="fill" className="text-[#7E1119]" /> Uncovered open shoots
          {data.open_gap_count > 0 && (
            <span className="ml-1 min-w-5 h-5 px-1.5 rounded-full bg-[#7E1119] text-white text-xs font-bold flex items-center justify-center" data-testid="open-gap-count">{data.open_gap_count}</span>
          )}
        </h3>
        {(!data.open_shoots || data.open_shoots.length === 0) ? (
          <div className="border border-dashed border-emerald-300 bg-emerald-50 rounded-lg p-4 text-sm text-emerald-700" data-testid="no-open-shoots">
            No open shoots waiting on crew right now. Every scheduled shoot is fully staffed.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3" data-testid="open-shoots-list">
            {data.open_shoots.map((s) => {
              const st = STATUS[s.severity] || STATUS.ok;
              const roles = [];
              if (s.needs_photographer) roles.push(`Photographer (${s.photographers_in_range} in range)`);
              if (s.needs_pilot) roles.push(`Pilot (${s.pilots_in_range} in range)`);
              return (
                <div key={s.id} data-testid={`open-shoot-${s.id}`}
                  className={`border rounded-lg p-3 ${st.soft}`}>
                  <div className="flex items-center justify-between gap-2">
                    <button onClick={() => onOpen && onOpen(s.id)} data-testid={`open-shoot-open-${s.id}`}
                      className="font-semibold text-[#1A1715] text-sm truncate text-left hover:underline">{s.title}</button>
                    <span className={`inline-block text-[10px] font-semibold uppercase px-2 py-0.5 rounded border ${st.soft} shrink-0`}>{st.label}</span>
                  </div>
                  <p className="text-xs text-[#1A1715]/55 flex items-center gap-1 mt-0.5 truncate"><MapPin size={11} weight="fill" />{s.city || s.zip || s.address}</p>
                  <p className="text-xs mt-1.5 opacity-80">{roles.join(" · ")}</p>
                  {!s.has_geo && <p className="text-[10px] text-[#7E1119] mt-1">No ZIP/location — can&rsquo;t map or match crew.</p>}
                  <Button data-testid={`assign-crew-btn-${s.id}`} size="sm" onClick={() => setAssignFor(s)}
                    className="mt-2.5 w-full bg-[#4A0000] hover:bg-[#5C1A1A] text-white h-8 text-xs">
                    <UserPlus size={13} weight="fill" className="mr-1" /> Assign or invite crew
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Prioritized gap alerts */}
      <div>
        <h3 className="text-sm font-semibold text-[#1A1715] mb-3 flex items-center gap-2"><Warning size={16} weight="fill" className="text-[#7E1119]" /> Priority gaps</h3>
        {data.alerts.length === 0 ? (
          <div className="border border-dashed border-emerald-300 bg-emerald-50 rounded-lg p-4 text-sm text-emerald-700" data-testid="coverage-no-gaps">
            No coverage gaps detected in this window. Every region with demand has a photographer in range.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3" data-testid="coverage-alerts">
            {data.alerts.map((a, i) => (
              <div key={i} data-testid={`coverage-alert-${i}`} className={`border rounded-lg p-3 ${STATUS[a.severity].soft}`}>
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide">
                  {a.type === "region" ? <MapPin size={13} weight="fill" /> : <Calendar size={13} weight="fill" />}{a.label}
                </div>
                <p className="text-xs mt-1 opacity-80">{a.detail}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Daily coverage strip */}
      <div>
        <h3 className="text-sm font-semibold text-[#1A1715] mb-3 flex items-center gap-2"><Calendar size={16} weight="fill" className="text-[#B08D57]" /> Daily availability</h3>
        <div className="flex gap-2 overflow-x-auto pb-2" data-testid="coverage-days">
          {data.days.map((d) => {
            const s = STATUS[d.status] || STATUS.ok;
            return (
              <div key={d.date} data-testid={`coverage-day-${d.date}`} className="flex-none w-[70px] border border-[#1A1715]/10 rounded-lg overflow-hidden text-center bg-white">
                <div className={`${s.bg} text-white py-1`}>
                  <div className="text-[10px] uppercase tracking-wide opacity-90">{d.weekday}</div>
                  <div className="text-lg font-bold leading-none">{d.day}</div>
                </div>
                <div className="py-2 px-1">
                  <div className="text-sm font-bold text-[#1A1715]" title="available photographers">{d.status === "holiday" ? "—" : d.available}</div>
                  <div className="text-[9px] text-[#1A1715]/50 uppercase">avail</div>
                  {d.demand > 0 && <div className="text-[9px] text-[#7E1119] font-semibold mt-1">{d.demand} booked</div>}
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex gap-4 text-xs text-[#1A1715]/60 mt-2">
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-[#7E1119]" /> Gap (0)</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-[#B08D57]" /> Tight (1)</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-emerald-600" /> OK (2+)</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-[#1A1715]/30" /> Holiday</span>
        </div>
      </div>

      {/* Regional coverage */}
      <div>
        <h3 className="text-sm font-semibold text-[#1A1715] mb-3 flex items-center gap-2"><MapPin size={16} weight="fill" className="text-[#7E1119]" /> Regional coverage (by ZIP &amp; driving radius)</h3>
        {data.regions.length === 0 ? (
          <p className="text-sm text-[#1A1715]/50">No upcoming shoots to map yet.</p>
        ) : (
          <div className="border border-[#1A1715]/10 rounded-lg overflow-hidden" data-testid="coverage-regions">
            <table className="w-full text-sm">
              <thead className="bg-[#F1EDE4] text-[#1A1715]/60 text-xs uppercase tracking-wide">
                <tr><th className="text-left px-4 py-2">Region</th><th className="text-center px-3 py-2">Demand</th><th className="text-center px-3 py-2">In range</th><th className="text-center px-3 py-2">Nearest</th><th className="text-right px-4 py-2">Status</th></tr>
              </thead>
              <tbody>
                {data.regions.map((r) => {
                  const s = STATUS[r.status];
                  return (
                    <tr key={r.zip} data-testid={`coverage-region-${r.zip}`} className="border-t border-[#1A1715]/8">
                      <td className="px-4 py-2.5 font-medium text-[#1A1715]">{r.city || "Unknown"} <span className="text-[#1A1715]/40 font-normal">{r.zip}</span></td>
                      <td className="text-center px-3">{r.demand}</td>
                      <td className="text-center px-3">{r.has_geo ? r.covered : "—"}</td>
                      <td className="text-center px-3 text-[#1A1715]/60">{r.nearest_miles != null ? `${r.nearest_miles} mi` : "—"}</td>
                      <td className="text-right px-4"><span className={`inline-block text-[10px] font-semibold uppercase px-2 py-0.5 rounded border ${s.soft}`}>{s.label}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Photographer roster */}
      <div>
        <h3 className="text-sm font-semibold text-[#1A1715] mb-3 flex items-center gap-2"><Users size={16} weight="fill" className="text-[#B08D57]" /> Photographer bases</h3>
        <div className="grid sm:grid-cols-2 gap-2" data-testid="coverage-photogs">
          {data.photographers.map((p, i) => (
            <div key={i} className="flex items-center justify-between border border-[#1A1715]/10 rounded-lg px-4 py-2.5 bg-white">
              <div>
                <p className="font-medium text-[#1A1715] text-sm">{p.name}</p>
                <p className="text-xs text-[#1A1715]/55">{p.has_base ? `${p.base_zip} · ${p.radius} mi radius` : <span className="text-[#B08D57] font-medium flex items-center gap-1"><Warning size={11} weight="fill" /> No base ZIP set</span>}</p>
              </div>
              <span className="text-xs text-[#1A1715]/50">{p.days_off} day(s) off</span>
            </div>
          ))}
        </div>
      </div>

      {/* Assign / invite crew dialog */}
      <Dialog open={!!assignFor} onOpenChange={(o) => !o && setAssignFor(null)}>
        <DialogContent className="max-w-lg" data-testid="assign-crew-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#1A1715]">Staff this shoot</DialogTitle>
          </DialogHeader>
          {assignFor && (
            <div className="space-y-4 max-h-[65vh] overflow-y-auto">
              <div>
                <p className="font-semibold text-[#1A1715] text-sm">{assignFor.title}</p>
                <p className="text-xs text-[#1A1715]/55 flex items-center gap-1"><MapPin size={11} weight="fill" />{assignFor.city || assignFor.zip || assignFor.address}</p>
              </div>
              {assignFor.needs_photographer && (
                <CandidateGroup label="Photographer" slot="photographer" list={assignFor.photo_candidates}
                  busy={busy} onAssign={doAssign} onInvite={doInvite} />
              )}
              {assignFor.needs_pilot && (
                <CandidateGroup label="Drone pilot" slot="pilot" list={assignFor.pilot_candidates}
                  busy={busy} onAssign={doAssign} onInvite={doInvite} />
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CandidateGroup({ label, slot, list, busy, onAssign, onInvite }) {
  return (
    <div data-testid={`candidate-group-${slot}`}>
      <h4 className="text-xs font-semibold uppercase tracking-wide text-[#B08D57] mb-2">{label}</h4>
      {(!list || list.length === 0) ? (
        <p className="text-xs text-[#1A1715]/50">No {label.toLowerCase()}s on the roster.</p>
      ) : (
        <div className="space-y-2">
          {list.map((c) => (
            <div key={c.id} data-testid={`candidate-${slot}-${c.id}`}
              className="flex items-center justify-between gap-2 border border-[#1A1715]/10 rounded-lg px-3 py-2 bg-white">
              <div className="min-w-0">
                <p className="text-sm font-medium text-[#1A1715] truncate">{c.name}</p>
                <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
                  {c.distance != null ? (
                    <span className="text-[11px] text-[#1A1715]/55 flex items-center gap-0.5"><NavigationArrow size={10} weight="duotone" />{c.distance} mi</span>
                  ) : <span className="text-[11px] text-[#1A1715]/40">No base ZIP</span>}
                  {c.in_range && <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px] px-1.5 py-0">In range</Badge>}
                  {c.available ? (
                    <Badge className="bg-[#B08D57]/15 text-[#8a6a3a] border-[#B08D57]/40 text-[10px] px-1.5 py-0">Available</Badge>
                  ) : (
                    <Badge className="bg-[#7E1119]/10 text-[#7E1119] border-[#7E1119]/30 text-[10px] px-1.5 py-0">Off that day</Badge>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <Button data-testid={`invite-${slot}-${c.id}`} size="sm" variant="outline" disabled={busy}
                  onClick={() => onInvite(slot, c.id)} className="h-7 text-xs border-[#B08D57]/50 text-[#8a6a3a] hover:bg-[#B08D57]/10">
                  <PaperPlaneTilt size={12} weight="fill" className="mr-1" />Invite
                </Button>
                <Button data-testid={`assign-${slot}-${c.id}`} size="sm" disabled={busy}
                  onClick={() => onAssign(slot, c.id)} className="h-7 text-xs bg-[#4A0000] hover:bg-[#5C1A1A] text-white">
                  <Check size={12} weight="bold" className="mr-1" />Assign
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
